import { mergeAttributes, Node } from '@tiptap/core'

/**
 * An atomic inline node for an `@Name` mention of an internal user.
 *
 * It renders as a chip in the editor and serializes to a span carrying
 * `data-mention` (the email) plus a literal `@Name` text label. The label is
 * what keeps the rest of the feature working unchanged:
 *
 *  - `AddNote` reconciles mentions on save with `content.includes('@' + name)`;
 *  - the notes list previews and the note search run `stripHtmlTags`, which
 *    leaves `@Name` behind;
 *  - the emailed copy of a note is the same HTML, so a recipient sees `@Name`.
 *
 * Being atomic is the point: a mention cannot be split by bolding half of it,
 * which is exactly how a plain-text `@Name` match breaks once the body can
 * carry formatting.
 */
export const Mention = Node.create({
  name: 'mention',

  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      name: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-mention-name') || '',
        renderHTML: (attrs) => ({ 'data-mention-name': attrs.name }),
      },
      email: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-mention') || '',
        renderHTML: (attrs) => ({ 'data-mention': attrs.email }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-mention]',
      },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    const name = (node.attrs as any).name || ''
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'rte-mention',
        contenteditable: 'false',
      }),
      `@${name}`,
    ]
  },

  renderText({ node }) {
    return `@${(node.attrs as any).name || ''}`
  },
})
